import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button, Form, FormGroup, Label, Input } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { updateEmailTemplate } from "../../../redux/states/settings/thunk";
import ButtonLoader from "../../../elements/ButtonLoader";
import { toast } from "react-toastify";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

const EditEmailTemplate = (props) => {
  const dispatch = useDispatch();
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [specialStrings, setSpecialStrings] = useState([]);
  const { getEmailTemplateList, onHide, setUpdateTemplate } = props;
  const { isEmailTemplateUpdate, showButtonLoader, selectedEmailTemplate } =
    useSelector((state) => state.settings);

  const extractSpecialStrings = (htmlContent) => {
    const specialStringRegex = /%([^%]+)%/g;
    const matches = [];
    let match;
    while ((match = specialStringRegex.exec(htmlContent)) !== null) {
      matches.push(match[0]);
    }

    return matches;
  };

  const checkMissingSpecialStrings = (htmlContent, expectedSpecialStrings) => {
    const extractedSpecialStrings = extractSpecialStrings(htmlContent);

    const missingSpecialStrings = expectedSpecialStrings.filter(
      (expectedSpecialString) =>
        !extractedSpecialStrings.includes(expectedSpecialString)
    );

    return missingSpecialStrings;
  };

  const getHtmlContent = () => {
    const contentState = editorState?.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();
    // Check if there is at least one block with non-empty text
    const hasContent = blocks.some((block) => block.getText().trim() !== "");
    if (!hasContent) {
      formik.setFieldError("editor", "Editor content is required.");
      return null;
    }
    const rawContentState = convertToRaw(contentState);
    const html = draftToHtml(rawContentState);
    return html;
  };

  const formik = useFormik({
    initialValues: {
      emailTitle: "",
    },
    onSubmit: async function (values, { resetForm }) {
      const htmlContent = getHtmlContent();
      if (!!htmlContent !== false) {
        const missingSpecialStrings = checkMissingSpecialStrings(
          htmlContent,
          specialStrings
        );
        if (missingSpecialStrings.length > 0) {
          formik.setFieldError(
            "editor",
            "Missing Special Strings:" + missingSpecialStrings
          );
        } else {
          const data = {
            ...values,
            body: htmlContent,
            title: selectedEmailTemplate?.title,
            emailTemplateId: selectedEmailTemplate?._id,
          };
          dispatch(updateEmailTemplate(data))
            .unwrap()
            .then((res) => {
              toast.success(res.message);
              resetForm();
              getEmailTemplateList();
              setUpdateTemplate(false);
            })
            .catch((err) => {
              toast.error(err.message);
              setUpdateTemplate(false);
            });
        }
      }
    },
    validationSchema: Yup.object({
      emailTitle: Yup.string().required("Subject is required"),
    }),
  });

  const setHtmlContentInEditor = (htmlContent) => {
    const blocksFromHtml = htmlToDraft(htmlContent);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );
    const editorStateData = EditorState.createWithContent(contentState);

    setEditorState(editorStateData);
  };

  useEffect(() => {
    if (isEmailTemplateUpdate) {
      formik.setValues({
        emailTitle: selectedEmailTemplate?.emailTitle,
      });
      setHtmlContentInEditor(selectedEmailTemplate?.body);
      setSpecialStrings(extractSpecialStrings(selectedEmailTemplate?.body));
    }
  }, [selectedEmailTemplate]);

  return (
    <>
      <Modal {...props} backdrop="static" keyboard={false} centered size="md">
        <Modal.Header className="border-0 position-relative">
          <Modal.Title className="w-100 text-black fs-6 text-start fw-bold py-1 px-3">
            Update Email Template
          </Modal.Title>
          <a href="javascript:void(0)" onClick={props.onHide}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
            >
              <g id="material-symbols:cancel-outline">
                <path
                  id="Vector"
                  d="M9.79967 19.8333L13.9997 15.6333L18.1997 19.8333L19.833 18.2L15.633 14L19.833 9.80001L18.1997 8.16668L13.9997 12.3667L9.79967 8.16668L8.16634 9.80001L12.3663 14L8.16634 18.2L9.79967 19.8333ZM13.9997 25.6667C12.3858 25.6667 10.8691 25.3602 9.44967 24.7473C8.03023 24.1345 6.79551 23.3034 5.74551 22.2542C4.69551 21.2042 3.86445 19.9695 3.25234 18.55C2.64023 17.1306 2.33379 15.6139 2.33301 14C2.33301 12.3861 2.63945 10.8695 3.25234 9.45001C3.86523 8.03057 4.69629 6.79584 5.74551 5.74584C6.79551 4.69584 8.03023 3.86479 9.44967 3.25268C10.8691 2.64057 12.3858 2.33412 13.9997 2.33334C15.6136 2.33334 17.1302 2.63979 18.5497 3.25268C19.9691 3.86557 21.2038 4.69662 22.2538 5.74584C23.3038 6.79584 24.1353 8.03057 24.7482 9.45001C25.3611 10.8695 25.6671 12.3861 25.6663 14C25.6663 15.6139 25.3599 17.1306 24.747 18.55C24.1341 19.9695 23.3031 21.2042 22.2538 22.2542C21.2038 23.3042 19.9691 24.1356 18.5497 24.7485C17.1302 25.3614 15.6136 25.6675 13.9997 25.6667ZM13.9997 23.3333C16.6052 23.3333 18.8122 22.4292 20.6205 20.6208C22.4288 18.8125 23.333 16.6056 23.333 14C23.333 11.3945 22.4288 9.18751 20.6205 7.37918C18.8122 5.57084 16.6052 4.66668 13.9997 4.66668C11.3941 4.66668 9.18717 5.57084 7.37884 7.37918C5.57051 9.18751 4.66634 11.3945 4.66634 14C4.66634 16.6056 5.57051 18.8125 7.37884 20.6208C9.18717 22.4292 11.3941 23.3333 13.9997 23.3333Z"
                  fill="#202020"
                ></path>
              </g>
            </svg>
          </a>
        </Modal.Header>
        <Modal.Body>
          <Form className="py-1 px-3 add_faqform">
            <FormGroup>
              <Label for="exampleEmail">Subject</Label>
              <Input
                type="text"
                onChange={formik.handleChange}
                value={formik.values.emailTitle}
                onBlur={formik.handleBlur}
                name="emailTitle"
                placeholder="Add subject"
                id="emailTitle"
              />
              {formik.touched.emailTitle && formik.errors.emailTitle && (
                <span className="text-danger">{formik.errors.emailTitle}</span>
              )}
            </FormGroup>
            <Editor
              editorStyle={{
                border: "1px solid #ddd",
                maxHeight: "350px",
                minHeight: "250px",
                overflow: "auto",
              }}
              editorState={editorState}
              onEditorStateChange={(newEditorState) =>
                setEditorState(newEditorState)
              }
            />
            {(formik.touched.editor || formik.errors.editor) && (
              <span className="text-danger">{formik.errors.editor}</span>
            )}

            {showButtonLoader ? (
              <Button
                onClick={formik.handleSubmit}
                className="bg-dark border-dark rounded py-2 text-white w-100 mt-4"
              >
                <ButtonLoader color={"text-light"} />
              </Button>
            ) : (
              <Button
                onClick={formik.handleSubmit}
                className="bg-dark border-dark rounded py-2 text-white w-100 mt-4"
              >
                Update
              </Button>
            )}
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};
export default EditEmailTemplate;
